import type { CSSProperties } from "react";
import type { CalendarEventType } from "./PropFirmCalendar";

/**
 * Case « enseigne » du calendrier prop firm, reprise du calendrier Lite
 * (`lite/pages/pfm/EventCalendar.tsx`) : fond d'encre, liseré à la couleur du
 * jour et halo sur les plus gros jours du mois. Réservée aux thèmes sombres,
 * où un halo se voit ; les thèmes clairs gardent les cases plates.
 *
 * `strength` ∈ [0, 1] : poids du jour par rapport au plus gros montant du mois.
 * Un jour sans montant (funded, reset) brille à 0.6, lisible sans dominer.
 */

/** Triplet HSL par type dominant, sous forme de variable CSS ou de valeur littérale. */
const TONE_HSL: Record<CalendarEventType, string> = {
  payout: "var(--edgely-pnl-positive)",
  expense: "var(--edgely-pnl-negative)",
  recurring: "var(--warning)",
  funded: "var(--primary)",
  reset: "258 90% 76%", // violet-400, sans variable de thème dédiée
};

export const neonColor = (type: CalendarEventType, alpha: number) => `hsl(${TONE_HSL[type]} / ${alpha})`;

export function neonStrength(amount: number, monthMax: number): number {
  if (amount === 0) return 0.6;
  return Math.min(1, Math.abs(amount) / Math.max(1, monthMax));
}

export function neonCellStyle(
  type: CalendarEventType,
  strength: number,
  opts: { today?: boolean } = {},
): CSSProperties {
  const ring = `inset 0 0 0 1px ${neonColor(type, 0.35 + 0.6 * strength)}`;
  const glow = strength > 0.5 ? `, 0 0 18px -5px ${neonColor(type, 0.9)}` : "";
  // `ring-*` de Tailwind passe par box-shadow : un style inline l'écraserait,
  // donc l'anneau « aujourd'hui » est recomposé ici.
  const todayRing = opts.today ? `, 0 0 0 1px hsl(var(--background)), 0 0 0 3px hsl(var(--primary))` : "";
  return {
    background: `linear-gradient(160deg, ${neonColor(type, 0.08 + 0.22 * strength)}, transparent 75%), hsl(var(--card))`,
    boxShadow: `${ring}${glow}${todayRing}`,
  };
}
